import React, { useMemo } from 'react';
import { useIntl } from 'react-intl';
import { TypedMemo } from '../../../common/types';
import type { CalendarPost, CalendarSong } from '../types';
import { CalendarFilterValues, countByState, NO_SONG } from './calendarFilterModel';
import { ChannelFilter } from './ChannelFilter';
import { SongFilterMenu } from './SongFilterMenu';
import { StateFilterChips } from './StateFilterChips';
/* eslint-disable css-modules/no-unused-class -- one stylesheet for the Calendar; each part uses its own slice of it */
import styles from './PromoteCalendar.module.scss';
/* eslint-enable css-modules/no-unused-class */

interface CalendarFiltersProps {
  /** Every post of the range, before the filters. */
  posts: CalendarPost[];
  songs: CalendarSong[];
  filters: CalendarFilterValues;
  isFiltered: boolean;
  onChange: (key: keyof CalendarFilterValues, values: string[]) => void;
  onClear: () => void;
}

function byRelease(a: CalendarSong, b: CalendarSong): number {
  // Upcoming first: the latest release date first, a song with no date last.
  return (b.releaseDate ?? '').localeCompare(a.releaseDate ?? '');
}

/** The Song menu, the channel tiles and the state chips (design a1, b8). They live in the address. */
const CalendarFiltersComponent: React.FC<CalendarFiltersProps> = ({
  posts,
  songs,
  filters,
  isFiltered,
  onChange,
  onClear,
}) => {
  const intl = useIntl();
  const sortedSongs = useMemo(() => [...songs].sort(byRelease), [songs]);
  const songCounts = useMemo(() => {
    const counts = new Map<string, number>();
    posts.forEach((post) => {
      const key = post.catalogItemUuid ?? NO_SONG;
      counts.set(key, (counts.get(key) ?? 0) + 1);
    });
    return counts;
  }, [posts]);
  const stateCounts = useMemo(() => countByState(posts), [posts]);

  return (
    <div className={styles.filters}>
      <SongFilterMenu
        songs={sortedSongs}
        counts={songCounts}
        selected={filters.songs}
        onChange={(values) => onChange('songs', values)}
      />
      <span className={styles.fsep} aria-hidden="true" />
      <ChannelFilter selected={filters.channels} onChange={(values) => onChange('channels', values)} />
      <span className={styles.fsep} aria-hidden="true" />
      <StateFilterChips
        counts={stateCounts}
        selected={filters.states}
        onChange={(values) => onChange('states', values)}
      />
      {isFiltered ? (
        <button type="button" className={styles.linkBtn} onClick={onClear}>
          {intl.formatMessage({ id: 'app.sml.promote.calendar.filter.clear', defaultMessage: 'Clear filters' })}
        </button>
      ) : null}
    </div>
  );
};

export const CalendarFilters = TypedMemo(CalendarFiltersComponent);
